/**
 * Construction modules (Admin Setup → Purchasing).
 *
 * Lets a customer admin switch the construction tenant's modules on and off
 * for their own tenant — monthly supplier uploads, spend dashboard, spend
 * leakage, division scoring, call-off performance and commodity codes —
 * through the client-editable feature-flags endpoint, the same route Allow
 * Supplier Decline uses. Each flag gates its sidebar items and endpoints.
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Loader2, Blocks } from 'lucide-react';
import { platformSettingsAPI, type ClientFeatureFlags } from '../../../lib/api';
import { toast } from 'sonner';

type ModuleKey = 'supplier_uploads' | 'performance_from_uploads' | 'spend_dashboard' | 'spend_leakage' | 'division_scoring' | 'call_off_performance' | 'commodity_codes';

const MODULES: Array<{ key: ModuleKey; label: string; description: string }> = [
  { key: 'supplier_uploads', label: 'Monthly supplier uploads', description: 'Data Upload, Supplier Upload History and the supplier\'s Monthly Uploads page, with reminders and chasing.' },
  { key: 'performance_from_uploads', label: 'Current Performance from supplier files', description: 'The Dashboard\'s Current Performance tab reads the monthly supplier uploads instead of PO lines (for tenants with no ERP feed): latest complete month by default, no Current Week.' },
  { key: 'spend_dashboard', label: 'Spend Dashboard', description: 'Spend Dashboard as its own side-menu page. The Dashboard\'s Spend tab shows the same charts either way.' },
  { key: 'spend_leakage', label: 'Spend Leakage', description: 'Spend outside each supplier\'s approved commodities, split by upload status.' },
  { key: 'division_scoring', label: 'Division scoring', description: 'Division Performance quarterly scoring and the Division Score Report.' },
  { key: 'call_off_performance', label: 'Call Off Performance', description: 'Week-bucket call-off analysis fed by the monthly Supplier Performance uploads (off for tenants that do not call off).' },
  { key: 'commodity_codes', label: 'Commodity codes', description: 'Commodity code administration used by parts and spend leakage.' },
];

export function ConstructionModulesSetting() {
  const [loading, setLoading] = useState(true);
  const [flags, setFlags] = useState<Partial<ClientFeatureFlags>>({});
  const [saving, setSaving] = useState<ModuleKey | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await platformSettingsAPI.getClientFeatureFlags();
        setFlags(data);
      } catch (err: unknown) {
        console.error('Error loading construction module flags:', err);
        toast.error('Failed to load the construction module settings');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleToggle = async (key: ModuleKey, checked: boolean) => {
    const previous = flags;
    setFlags({ ...flags, [key]: checked });
    setSaving(key);
    try {
      const { data } = await platformSettingsAPI.patchClientFeatureFlags({ [key]: checked });
      setFlags(data);
      const label = MODULES.find((m) => m.key === key)?.label ?? key;
      toast.success(checked ? `${label} switched on` : `${label} switched off`);
    } catch (err: unknown) {
      console.error('Error saving construction module flag:', err);
      toast.error('Failed to save the module setting');
      setFlags(previous);
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="construction-modules">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Blocks className="h-5 w-5" />
          Construction modules
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Switch the modules your business uses on or off. Each one adds or removes its own menu items for customers and suppliers.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {MODULES.map(({ key, label, description }) => (
          <div key={key} className="flex items-center justify-between rounded-md border border-input bg-background px-4 py-3">
            <div className="space-y-1 pr-3">
              <Label htmlFor={`module-${key}`} className="text-sm font-medium">{label}</Label>
              <p className="text-xs text-muted-foreground">{description}</p>
            </div>
            <Switch
              id={`module-${key}`}
              checked={!!flags[key]}
              disabled={saving !== null}
              onCheckedChange={(checked) => handleToggle(key, checked)}
            />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
